import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Password strength calculator
function calculatePasswordStrength(password: string): { score: number; label: string; color: string } {
  let score = 0;
  
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (score <= 2) return { score: 1, label: '약함', color: 'bg-red-500' };
  if (score <= 4) return { score: 2, label: '보통', color: 'bg-yellow-500' };
  return { score: 3, label: '강함', color: 'bg-green-500' };
}

// Format phone number
function formatPhoneNumber(value: string): string {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
}

interface FieldErrors {
  email?: string;
  password?: string;
  passwordConfirm?: string;
  display_name?: string;
  phone?: string;
  birthdate?: string;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromDownload = (location.state as { from?: string })?.from === '/download';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    display_name: '',
    phone: '',
    birthdate: '',
    terms_agreed: false,
    privacy_agreed: false,
    marketing_agreed: false,
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Validation functions
  const validateEmail = useCallback((value: string): string | undefined => {
    if (!value) return '이메일을 입력해주세요';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return '올바른 이메일 형식이 아닙니다 (예: user@example.com)';
    return undefined;
  }, []);

  const validatePassword = useCallback((value: string): string | undefined => {
    if (!value) return '비밀번호를 입력해주세요';
    if (value.length < 8) return '비밀번호는 8자 이상이어야 합니다';
    if (!/[a-zA-Z]/.test(value)) return '영문자를 포함해주세요';
    if (!/[0-9]/.test(value)) return '숫자를 포함해주세요';
    return undefined;
  }, []);

  const validatePasswordConfirm = useCallback((value: string, password: string): string | undefined => {
    if (!value) return '비밀번호 확인을 입력해주세요';
    if (value !== password) return '비밀번호가 일치하지 않습니다';
    return undefined;
  }, []);

  const validateDisplayName = useCallback((value: string): string | undefined => {
    if (!value) return '이름을 입력해주세요';
    if (value.length < 2) return '이름은 2자 이상이어야 합니다';
    return undefined;
  }, []);

  const validatePhone = useCallback((value: string): string | undefined => {
    if (!value) return '연락처를 입력해주세요';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length < 10 || numbers.length > 11) return '올바른 전화번호 형식이 아닙니다';
    return undefined;
  }, []);

  const validateBirthdate = useCallback((value: string): string | undefined => {
    if (!value) return '생년월일을 입력해주세요';
    const birthDate = new Date(value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 14) return '만 14세 이상만 가입할 수 있습니다';
    return undefined;
  }, []);

  // Real-time validation
  useEffect(() => {
    const errors: FieldErrors = {};
    if (touched.email) errors.email = validateEmail(formData.email);
    if (touched.password) errors.password = validatePassword(formData.password);
    if (touched.passwordConfirm) errors.passwordConfirm = validatePasswordConfirm(formData.passwordConfirm, formData.password);
    if (touched.display_name) errors.display_name = validateDisplayName(formData.display_name);
    if (touched.phone) errors.phone = validatePhone(formData.phone);
    if (touched.birthdate) errors.birthdate = validateBirthdate(formData.birthdate);
    setFieldErrors(errors);
  }, [formData, touched, validateEmail, validatePassword, validatePasswordConfirm, validateDisplayName, validatePhone, validateBirthdate]);

  // Password strength
  const passwordStrength = useMemo(() => {
    if (!formData.password) return null;
    return calculatePasswordStrength(formData.password);
  }, [formData.password]);

  // Password match indicator
  const passwordsMatch = useMemo(() => {
    if (!formData.passwordConfirm) return null;
    return formData.password === formData.passwordConfirm;
  }, [formData.password, formData.passwordConfirm]);

  // Form validity
  const isFormValid = useMemo(() => {
    return (
      formData.email &&
      formData.password &&
      formData.passwordConfirm &&
      formData.display_name &&
      formData.phone &&
      formData.birthdate &&
      formData.terms_agreed &&
      formData.privacy_agreed &&
      !validateEmail(formData.email) &&
      !validatePassword(formData.password) &&
      !validatePasswordConfirm(formData.passwordConfirm, formData.password) &&
      !validateDisplayName(formData.display_name) &&
      !validatePhone(formData.phone) &&
      !validateBirthdate(formData.birthdate)
    );
  }, [formData, validateEmail, validatePassword, validatePasswordConfirm, validateDisplayName, validatePhone, validateBirthdate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'phone') {
      setFormData(prev => ({
        ...prev,
        phone: formatPhoneNumber(value),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Touch all fields to show validation
    setTouched({
      email: true,
      password: true,
      passwordConfirm: true,
      display_name: true,
      phone: true,
      birthdate: true,
    });

    // Validate all fields
    const errors: FieldErrors = {
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
      passwordConfirm: validatePasswordConfirm(formData.passwordConfirm, formData.password),
      display_name: validateDisplayName(formData.display_name),
      phone: validatePhone(formData.phone),
      birthdate: validateBirthdate(formData.birthdate),
    };

    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) {
      setFieldErrors(errors);
      return;
    }

    if (!formData.terms_agreed || !formData.privacy_agreed) {
      setError('필수 약관에 동의해주세요');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        display_name: formData.display_name,
        phone: formData.phone.replace(/\D/g, ''),
        birthdate: formData.birthdate,
        terms_agreed: formData.terms_agreed,
        privacy_agreed: formData.privacy_agreed,
        marketing_agreed: formData.marketing_agreed,
      });
      navigate('/verify-email', { state: { email: formData.email } });
    } catch (err) {
      const message = err instanceof Error ? err.message : '회원가입에 실패했습니다';
      if (message.includes('already') || message.includes('exists')) {
        setError('이미 가입된 이메일입니다. 로그인하거나 다른 이메일을 사용해주세요.');
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAllAgree = (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      terms_agreed: checked,
      privacy_agreed: checked,
      marketing_agreed: checked,
    }));
  };

  const allChecked = formData.terms_agreed && formData.privacy_agreed && formData.marketing_agreed;

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">회원가입</h1>
          <p className="text-gray-700 dark:text-gray-300">moldClaw와 함께 시작하세요</p>
        </div>

        {/* Download flow guidance */}
        {fromDownload && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-xl">🎯</span>
              <div>
                <h4 className="font-medium text-green-800 dark:text-green-200 mb-1">거의 다 됐어요!</h4>
                <p className="text-sm text-green-700 dark:text-green-300">
                  회원가입 → 이메일 인증 후 바로 다운로드할 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <div 
                className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm"
                role="alert"
                aria-live="polite"
              >
                {error}
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                이메일 <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(필수)</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                className={`input ${touched.email && fieldErrors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="email@example.com"
                autoComplete="email"
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                aria-invalid={touched.email && !!fieldErrors.email}
                required
              />
              {touched.email && fieldErrors.email && (
                <p id="email-error" className="mt-1 text-sm text-red-500" role="alert">
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                비밀번호 <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(필수)</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('password')}
                  className={`input pr-12 ${touched.password && fieldErrors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="영문, 숫자 포함 8자 이상"
                  autoComplete="new-password"
                  aria-describedby="password-requirements password-error"
                  aria-invalid={touched.password && !!fieldErrors.password}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-space-blue rounded p-1"
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {/* Password Strength Indicator */}
              {passwordStrength && (
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 3) * 100}%` }}
                        role="progressbar"
                        aria-valuenow={passwordStrength.score}
                        aria-valuemin={0}
                        aria-valuemax={3}
                        aria-label="비밀번호 강도"
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score === 1 ? 'text-red-500' :
                      passwordStrength.score === 2 ? 'text-yellow-600 dark:text-yellow-500' :
                      'text-green-500'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p id="password-requirements" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    영문, 숫자, 특수문자를 조합하면 더 안전합니다
                  </p>
                </div>
              )}
              {touched.password && fieldErrors.password && (
                <p id="password-error" className="mt-1 text-sm text-red-500" role="alert">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            {/* Password Confirm */}
            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                비밀번호 확인 <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(필수)</span>
              </label>
              <div className="relative">
                <input
                  id="passwordConfirm"
                  name="passwordConfirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  onBlur={() => handleBlur('passwordConfirm')}
                  className={`input pr-12 ${touched.passwordConfirm && fieldErrors.passwordConfirm ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="비밀번호 재입력"
                  autoComplete="new-password"
                  aria-describedby={fieldErrors.passwordConfirm ? 'passwordConfirm-error' : 'passwordConfirm-hint'}
                  aria-invalid={touched.passwordConfirm && !!fieldErrors.passwordConfirm}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-space-blue rounded p-1"
                  aria-label={showPasswordConfirm ? '비밀번호 숨기기' : '비밀번호 표시'}
                >
                  {showPasswordConfirm ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {/* Password match indicator */}
              {passwordsMatch !== null && (
                <p 
                  id="passwordConfirm-hint"
                  className={`mt-1 text-sm flex items-center gap-1 ${passwordsMatch ? 'text-green-500' : 'text-red-500'}`}
                >
                  {passwordsMatch ? (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      비밀번호가 일치합니다
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      비밀번호가 일치하지 않습니다
                    </>
                  )}
                </p>
              )}
              {touched.passwordConfirm && fieldErrors.passwordConfirm && !passwordsMatch && (
                <p id="passwordConfirm-error" className="mt-1 text-sm text-red-500" role="alert">
                  {fieldErrors.passwordConfirm}
                </p>
              )}
            </div>

            {/* Display Name */}
            <div>
              <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                이름 <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(필수)</span>
              </label>
              <input
                id="display_name"
                name="display_name"
                type="text"
                value={formData.display_name}
                onChange={handleChange}
                onBlur={() => handleBlur('display_name')}
                className={`input ${touched.display_name && fieldErrors.display_name ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="홍길동"
                autoComplete="name"
                aria-describedby={fieldErrors.display_name ? 'display_name-error' : undefined}
                aria-invalid={touched.display_name && !!fieldErrors.display_name}
                required
              />
              {touched.display_name && fieldErrors.display_name && (
                <p id="display_name-error" className="mt-1 text-sm text-red-500" role="alert">
                  {fieldErrors.display_name}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                연락처 <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(필수)</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={() => handleBlur('phone')}
                className={`input ${touched.phone && fieldErrors.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="010-1234-5678"
                autoComplete="tel"
                aria-describedby={fieldErrors.phone ? 'phone-error' : undefined}
                aria-invalid={touched.phone && !!fieldErrors.phone}
                required
              />
              {touched.phone && fieldErrors.phone && (
                <p id="phone-error" className="mt-1 text-sm text-red-500" role="alert">
                  {fieldErrors.phone}
                </p>
              )}
            </div>

            {/* Birthdate */}
            <div>
              <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                생년월일 <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(필수)</span>
              </label>
              <input
                id="birthdate"
                name="birthdate"
                type="date"
                value={formData.birthdate}
                onChange={handleChange}
                onBlur={() => handleBlur('birthdate')}
                className={`input ${touched.birthdate && fieldErrors.birthdate ? 'border-red-500 focus:ring-red-500' : ''}`}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0]}
                autoComplete="bday"
                aria-describedby="birthdate-hint birthdate-error"
                aria-invalid={touched.birthdate && !!fieldErrors.birthdate}
                required
              />
              <p id="birthdate-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                만 14세 이상만 가입할 수 있습니다
              </p>
              {touched.birthdate && fieldErrors.birthdate && (
                <p id="birthdate-error" className="mt-1 text-sm text-red-500" role="alert">
                  {fieldErrors.birthdate}
                </p>
              )}
            </div>

            {/* Agreement */}
            <fieldset className="pt-4 border-t border-gray-200 dark:border-zinc-700">
              <legend className="sr-only">약관 동의</legend>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => handleAllAgree(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 dark:border-zinc-600 text-space-blue focus:ring-space-blue"
                    aria-label="전체 동의"
                  />
                  <span className="font-medium text-gray-900 dark:text-white">전체 동의</span>
                </label>

                <div className="pl-7 space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="terms_agreed"
                      checked={formData.terms_agreed}
                      onChange={handleChange}
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 dark:border-zinc-600 text-space-blue focus:ring-space-blue"
                      aria-describedby="terms-label"
                    />
                    <span id="terms-label" className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="text-red-500 font-medium">[필수]</span>{' '}
                      <Link to="/terms" className="text-space-blue hover:underline focus:outline-none focus:ring-2 focus:ring-space-blue rounded" target="_blank">
                        이용약관
                      </Link>
                      에 동의합니다
                    </span>
                  </label>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="privacy_agreed"
                      checked={formData.privacy_agreed}
                      onChange={handleChange}
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 dark:border-zinc-600 text-space-blue focus:ring-space-blue"
                      aria-describedby="privacy-label"
                    />
                    <span id="privacy-label" className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="text-red-500 font-medium">[필수]</span>{' '}
                      <Link to="/privacy" className="text-space-blue hover:underline focus:outline-none focus:ring-2 focus:ring-space-blue rounded" target="_blank">
                        개인정보 처리방침
                      </Link>
                      에 동의합니다
                    </span>
                  </label>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="marketing_agreed"
                      checked={formData.marketing_agreed}
                      onChange={handleChange}
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 dark:border-zinc-600 text-space-blue focus:ring-space-blue"
                      aria-describedby="marketing-label"
                    />
                    <span id="marketing-label" className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="text-gray-500">[선택]</span> 마케팅 정보 수신에 동의합니다
                    </span>
                  </label>
                </div>
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={isLoading || !isFormValid}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-space-blue focus:ring-offset-2"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  가입 중...
                </span>
              ) : '회원가입'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-700 dark:text-gray-300">
            이미 계정이 있으신가요?{' '}
            <Link 
              to="/login" 
              className="text-space-blue font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-space-blue focus:ring-offset-2 rounded"
            >
              로그인
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
